import React, { useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ContentBlockList } from "@/modules/hub/features/ResourceManagement/components/ContentBlockList/ContentBlockList";
import { ContentBlockEditorProps } from "@/modules/hub/features/ResourceManagement/components/ContentBlockEditor/types";
import {
  BlockType,
  ContentBlockBase,
} from "@/modules/hub/features/ResourceManagement/components/SortableBlock/types";

const BLOCK_BUTTONS: { type: BlockType; label: string }[] = [
  { type: "text", label: "Add Text" },
  { type: "image", label: "Add Image" },
  { type: "file", label: "Add File" },
  { type: "link", label: "Add Link" },
  { type: "video", label: "Add Video" },
  { type: "audio", label: "Add Audio" },
  { type: "resource_reference", label: "Add Resource References" },
  { type: "faq", label: "Add FAQ" },
  { type: "columns", label: "Add Columns" },
  { type: "quote", label: "Add Quote" },
  { type: "heading", label: "Add Heading" },
];

export function ContentBlockEditor({
  blocks,
  onChange,
  allowedTypes,
}: ContentBlockEditorProps) {
  const listContainerRef = useRef<HTMLDivElement>(null);

  const handleAddBlock = useCallback(
    (blockType: BlockType) => {
      const newBlock: ContentBlockBase = {
        id: crypto.randomUUID(),
        type: blockType,
        content: "",
        order_number: blocks.length,
      };

      const updated = [...blocks, newBlock];
      onChange(updated);

      setTimeout(() => {
        if (listContainerRef.current) {
          listContainerRef.current.scrollTo({
            top: listContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }, 0);
    },
    [blocks, onChange],
  );

  const handleBlocksChange = useCallback(
    (updatedBlocks: ContentBlockBase[]) => {
      onChange(updatedBlocks);
    },
    [onChange],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {BLOCK_BUTTONS.filter(
          (b) => !allowedTypes || allowedTypes.includes(b.type),
        ).map((b) => (
          <Button
            key={b.type}
            onClick={() => handleAddBlock(b.type)}
            size="sm"
            type="button"
            variant="outline"
          >
            {b.label}
          </Button>
        ))}
      </div>

      <ContentBlockList
        blocks={blocks}
        onChange={handleBlocksChange}
        ref={listContainerRef}
      />
    </div>
  );
}
